import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Stack } from '@mui/material';
import { InlineAlert } from '@components/atoms/InlineAlert';
import { FileUploader } from '@components/molecules/FileUploader';
import { FileUploadStatusWrapper } from '@components/organisms/FileUploadStatusWrapper';
import { UnifiedFileUploadService } from '@services/upload/FileUploadService';
import type {
  UnifiedUploadItem,
  UploadConfig,
} from '@services/upload/FileUploadService';
import type {
  DocumentUploadManagerProps,
  ValidationRules,
  FileUploadStatus,
} from './DocumentUploadManager.props';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { getJwtToken, getUserSessionId } from '@utils/security';
import { INSTANCE_URL } from '../../../config/env';
import {
  DEFAULT_VALIDATION_RULES,
  validateFiles,
} from './DocumentUploadManager.utils';

export const DocumentUploadManager: React.FC<DocumentUploadManagerProps> = ({
  validationRules = {},
  onFilesAdded,
  onFileDeleted,
  onFileRetry,
  onUploadComplete,
  onUploadError,
  uploadUrl = `${INSTANCE_URL}/knowledge_hub/api/v1/collections/4bd75837-b898-4ef6-adbc-e2a63e16fc3a/newdocuments`,
  authHeaders: providedAuthHeaders,
  disabled = false,
  sx,
}) => {
  const { showSnackbar } = useSnackbar();

  // Simplified authentication with validation
  const jwtToken = getJwtToken();
  const userSessionId = getUserSessionId();

  const authHeaders = useMemo(() => {
    if (providedAuthHeaders) return providedAuthHeaders;

    if (!jwtToken) {
      console.log('No JWT token available for upload authentication');
    }

    return {
      ...(jwtToken && { Authorization: `Bearer ${jwtToken}` }),
      ...(userSessionId && { 'x-user-session-id': userSessionId }),
    };
  }, [providedAuthHeaders, jwtToken, userSessionId]);

  const [displayFiles, setDisplayFiles] = useState<UnifiedUploadItem[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<
    { file: File; error: string }[]
  >([]);

  // Multiple independent alert states
  const [fileSizeExceedAlert, setFileSizeExceedAlert] = useState<{
    show: boolean;
    fileNames: string[];
    message: string;
  }>({ show: false, fileNames: [], message: '' });

  const [batchSizeExceedAlert, setBatchSizeExceedAlert] = useState<{
    show: boolean;
    message: string;
    totalSize?: number;
  }>({ show: false, message: '' });

  const uploadService = UnifiedFileUploadService.getInstance();

  const mergedRules: ValidationRules = useMemo(
    () => ({
      maxFiles: validationRules?.maxFiles ?? DEFAULT_VALIDATION_RULES.maxFiles,
      maxFileSize:
        validationRules?.maxFileSize ?? DEFAULT_VALIDATION_RULES.maxFileSize,
      maxBatchSize:
        validationRules?.maxBatchSize ?? DEFAULT_VALIDATION_RULES.maxBatchSize,
      acceptedExtensions:
        validationRules?.acceptedExtensions ??
        DEFAULT_VALIDATION_RULES.acceptedExtensions,
    }),
    [validationRules]
  );

  const handleUploadComplete = useCallback(
    (_fileId: string) => {
      // Check if all uploads are complete
      const allFiles = uploadService.getAllFiles();
      const pendingOrUploading = allFiles.filter((f) =>
        ['pending', 'stored', 'storing', 'uploading'].includes(f.status)
      );
      const failedFiles = allFiles.filter((f) => f.status === 'failed');

      // If no more pending/uploading files, show completion message
      if (pendingOrUploading.length === 0) {
        setTimeout(() => {
          const message =
            failedFiles.length === 0
              ? 'All files uploaded successfully!'
              : `${failedFiles.length} file(s) failed to upload`;

          showSnackbar(message, {
            variant: failedFiles.length === 0 ? 'success' : 'error',
          });

          // Notify parent only once when whole upload session completes
          const uploadedCount = allFiles.filter(
            (f) => f.status === 'uploaded'
          ).length;
          onUploadComplete?.(uploadedCount);
        }, 100);
      }
    },
    [showSnackbar, uploadService, onUploadComplete]
  );

  const handleUploadError = useCallback((_fileId: string, _error: string) => {
    // Upload errors are now handled by snackbar in FileUploadService
    // No additional processing needed here
  }, []);

  // Modal always starts with empty file list - never show existing files on reopen
  // Background uploads continue in the worker, but UI starts fresh each time
  useEffect(() => {
    setDisplayFiles([]);
  }, []);

  // Configure upload settings once when component mounts
  // This enables auto-enqueue when files are added
  useEffect(() => {
    const config: UploadConfig = {
      endpoint: uploadUrl,
      bearerToken: jwtToken || undefined,
      headers: authHeaders,
      maxRetries: 3,
    };
    uploadService.setUploadConfig(config);
  }, [uploadService, uploadUrl, jwtToken, authHeaders]);

  useEffect(() => {
    // Helper function to update a specific file in displayFiles
    const updateDisplayFile = (
      itemId: string,
      updates: Partial<UnifiedUploadItem>
    ) => {
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === itemId);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], ...updates };
          return updated;
        }
        return prev;
      });
    };

    // Set up event listeners that only respond to files in current modal session
    const handleStored = (item: UnifiedUploadItem) => {
      // Only update if this file is part of current modal session
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === item.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'stored',
            updatedAt: item.updatedAt,
          };
          return updated;
        }
        return prev;
      });
    };

    const handleProgress = (item: UnifiedUploadItem) => {
      // Only update if this file is part of current modal session
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === item.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'uploading',
            progress: item.progress,
            updatedAt: item.updatedAt,
          };
          return updated;
        }
        return prev;
      });
    };

    const handleComplete = (item: UnifiedUploadItem) => {
      // Only handle completion if this file is part of current modal session
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === item.id);
        if (index >= 0) {
          handleUploadComplete(item.id);
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'uploaded',
            progress: 100,
            updatedAt: item.updatedAt,
          };
          onUploadComplete?.(1);
          return updated;
        }
        return prev;
      });
    };

    const handleError = (item: UnifiedUploadItem) => {
      // Only handle error if this file is part of current modal session
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === item.id);
        if (index >= 0) {
          console.log(`Upload error for ${item.file.name}:`, item.error);
          const errorMsg = item.error || 'Unknown error';
          handleUploadError(item.id, errorMsg);
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'failed',
            error: errorMsg,
            updatedAt: item.updatedAt,
          };
          onUploadError?.(item.id, errorMsg);
          return updated;
        }
        return prev;
      });
    };

    const handleAborted = async (item: UnifiedUploadItem) => {
      // Only handle abort if this file is part of current modal session
      setDisplayFiles((prev) => {
        const index = prev.findIndex((f) => f.id === item.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            updatedAt: item.updatedAt,
          };
          return updated;
        }
        return prev;
      });
    };

    // Add permanent listeners including 'stored' event
    uploadService.addEventListener('stored', handleStored);
    uploadService.addEventListener('progress', handleProgress);
    uploadService.addEventListener('complete', handleComplete);
    uploadService.addEventListener('error', handleError);
    uploadService.addEventListener('aborted', handleAborted);

    // Cleanup on unmount
    return () => {
      uploadService.removeEventListener('stored', handleStored);
      uploadService.removeEventListener('progress', handleProgress);
      uploadService.removeEventListener('complete', handleComplete);
      uploadService.removeEventListener('error', handleError);
      uploadService.removeEventListener('aborted', handleAborted);
    };
  }, [
    uploadService,
    handleUploadComplete,
    handleUploadError,
    onUploadComplete,
    onUploadError,
  ]);

  const handleFileSelect = useCallback(
    async (selectedFiles: File[]) => {
      try {
        // Don't clear previous alerts - allow multiple alerts to coexist
        // Only clear specific alerts when they become invalid or succeed

        // Calculate total size of all selected files
        const totalSizeBytes = selectedFiles.reduce(
          (sum, file) => sum + file.size,
          0
        );
        const totalSizeMB = totalSizeBytes / (1024 * 1024);

        // Check if total size exceeds 500MB limit
        if (totalSizeMB > 500) {
          // Show rejected files in UI for user visibility
          const rejectedFilesForSize = selectedFiles.map((file) => ({
            file,
            error: `Rejected due to batch size limit (Total: ${totalSizeMB.toFixed(2)}MB > 500MB)`,
          }));

          setRejectedFiles((prev) => [...prev, ...rejectedFilesForSize]);
          setBatchSizeExceedAlert({
            show: true,
            message: `Total upload size exceeds the 500MB limit (${totalSizeMB.toFixed(2)}MB). ${selectedFiles.length} files rejected. Please select smaller files or upload in smaller batches.`,
            totalSize: totalSizeMB,
          });
          return; // Don't process any files for upload
        } else {
          // Clear batch size alert only if this batch is valid
          setBatchSizeExceedAlert({ show: false, message: '' });
        }

        // Validate this selection independently of any active/background uploads
        // Rationale: When the modal is reopened while uploads continue in the background,
        // we should allow the newly selected files to be queued without being penalized
        // by existing session counts/sizes. The worker + service enforce concurrency
        // and batch handling downstream.
        const existingFiles: {
          id: string;
          file: File;
          status: any;
          progress: number;
          error?: string;
        }[] = [];

        const validation = validateFiles(
          selectedFiles,
          existingFiles,
          mergedRules
        );

        // Separate valid and invalid files
        const validFiles: File[] = [];
        const newRejectedFiles: { file: File; error: string }[] = [];
        const fileSizeExceededFiles: string[] = [];
        const unsupportedTypeFiles: string[] = [];

        for (const file of selectedFiles) {
          const error = validation.errors.find((e) => e.fileName === file.name);

          if (error) {
            newRejectedFiles.push({ file, error: error.message });

            // Categorize errors for appropriate alerts
            if (
              error.message.includes('file type') ||
              error.message.includes('extension') ||
              error.message.includes('not supported') ||
              error.message.includes('invalid format')
            ) {
              unsupportedTypeFiles.push(file.name);
            } else if (
              error.message.includes('exceeds') &&
              error.message.includes('limit')
            ) {
              fileSizeExceededFiles.push(file.name);
            }
          } else {
            validFiles.push(file);
          }
        }

        // Show file size exceed alert
        if (fileSizeExceededFiles.length > 0) {
          setFileSizeExceedAlert((prev) => ({
            show: true,
            fileNames: [
              ...new Set([...prev.fileNames, ...fileSizeExceededFiles]),
            ], // Avoid duplicates
            message: `${fileSizeExceededFiles.length === 1 ? 'File' : 'Files'} exceed${fileSizeExceededFiles.length === 1 ? 's' : ''} the maximum file size limit`,
          }));
        }

        // Unsupported file types will show error in FileUploadStatusWrapper
        // No need for additional alert

        // Update rejected files state - avoid duplicates by file name and size
        if (newRejectedFiles.length > 0) {
          setRejectedFiles((prev) => {
            const existing = prev.map(
              (rf) => `${rf.file.name}_${rf.file.size}`
            );
            const filtered = newRejectedFiles.filter(
              (rf) => !existing.includes(`${rf.file.name}_${rf.file.size}`)
            );
            return [...prev, ...filtered];
          });
        }

        // Add valid files to local state and delegate storage to worker
        if (validFiles.length > 0) {
          // Create upload items in local state with pending status
          const newItems: UnifiedUploadItem[] = validFiles.map((file) => {
            const fileId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
            return {
              id: fileId,
              file,
              metadata: {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
              },
              status: 'pending' as const,
              progress: 0,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
          });

          // Add to display immediately
          setDisplayFiles((prev) => [...prev, ...newItems]);

          // Send files to worker for OPFS/IndexedDB storage and upload
          uploadService.addItems(newItems, {
            endpoint: uploadUrl,
            bearerToken: jwtToken || undefined,
            headers: authHeaders,
            maxRetries: 3,
          });

          onFilesAdded?.(
            newItems.map((item) => ({
              id: item.id,
              file: item.file,
              status: item.status as FileUploadStatus,
              progress: item.progress,
              error: item.error,
            }))
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        showSnackbar(`Failed to add files: ${errorMessage}`, {
          variant: 'error',
        });
      }
    },
    [
      mergedRules,
      uploadService,
      uploadUrl,
      jwtToken,
      authHeaders,
      onFilesAdded,
      showSnackbar,
    ]
  );

  const handleFileRetry = useCallback(
    async (fileId: string) => {
      const fileItem = uploadService.getFile(fileId);
      if (!fileItem) return;

      // Update displayFiles
      setDisplayFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, status: 'stored' as const, progress: 0, error: undefined }
            : f
        )
      );

      // Re-enqueue to worker for retry
      uploadService.retryUpload(fileId, {
        endpoint: uploadUrl,
        bearerToken: jwtToken || undefined,
        headers: authHeaders,
        maxRetries: 3,
      });

      onFileRetry?.(fileId);

      showSnackbar(`Retry initiated for "${fileItem.file.name}"`, {
        variant: 'info',
      });
    },
    [uploadService, uploadUrl, jwtToken, authHeaders, onFileRetry, showSnackbar]
  );

  const handleFilesSelected = useCallback(
    async (selectedFiles: File[] | FileList) => {
      const fileArray = Array.from(selectedFiles);
      await handleFileSelect(fileArray);
    },
    [handleFileSelect]
  );

  const handleFileDelete = useCallback(
    async (fileId: string) => {
      // Remove from upload service (this also removes from worker queue if pending)
      const success = await uploadService.removeFile(fileId);

      if (success) {
        // Remove from displayFiles
        setDisplayFiles((prev) => prev.filter((f) => f.id !== fileId));
        onFileDeleted?.(fileId);
      } else {
        // Check if it's in rejected files
        const file = uploadService.getFile(fileId);
        const rejectedIndex = rejectedFiles.findIndex(
          (rf) => rf.file.name === file?.file.name
        );
        if (rejectedIndex >= 0) {
          setRejectedFiles((prev) =>
            prev.filter((_, index) => index !== rejectedIndex)
          );
          onFileDeleted?.(fileId);
        }
      }
    },
    [uploadService, onFileDeleted, rejectedFiles]
  );

  // InlineAlert messages now persist until next upload (no auto-clear)
  // Messages are only cleared when:
  // 1. User manually closes the alert
  // 2. New file upload operation starts (cleared at beginning of handleFileSelect)

  // Display all files (from displayFiles + rejected files as display items)
  const allDisplayFiles = useMemo(() => {
    const uploadFiles = displayFiles;
    const rejectedDisplayFiles = rejectedFiles.map((rf, index) => ({
      id: `rejected_${rf.file.name}_${rf.file.size}_${rf.file.lastModified}_${index}`,
      file: rf.file,
      metadata: {
        name: rf.file.name,
        size: rf.file.size,
        type: rf.file.type,
        lastModified: rf.file.lastModified,
      },
      status: 'failed' as const,
      progress: 0,
      error: rf.error,
      createdAt: new Date(),
      updatedAt: new Date(),
    }));
    return [...uploadFiles, ...rejectedDisplayFiles];
  }, [displayFiles, rejectedFiles]);

  return (
    <Stack spacing={3} sx={sx}>
      {/* File Size Exceed Alert */}
      {fileSizeExceedAlert.show && (
        <InlineAlert
          variant="error"
          message={`${fileSizeExceedAlert.fileNames.length === 1 ? 'File exceeds' : 'Files exceed'} the maximum file size limit: ${fileSizeExceedAlert.fileNames.join(', ')}`}
          closable={true}
          onClose={() =>
            setFileSizeExceedAlert({ show: false, fileNames: [], message: '' })
          }
        />
      )}

      {/* Batch Size Exceed Alert */}
      {batchSizeExceedAlert.show && (
        <InlineAlert
          variant="warning"
          message={batchSizeExceedAlert.message}
          closable={true}
          onClose={() => setBatchSizeExceedAlert({ show: false, message: '' })}
        />
      )}

      <FileUploader
        onFilesSelected={handleFilesSelected}
        disabled={disabled}
        acceptedExtensions={mergedRules.acceptedExtensions}
        maxFiles={mergedRules.maxFiles}
      />

      {allDisplayFiles.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {allDisplayFiles.map((fileData) => {
            // Map service status to UI component status
            const uiStatus =
              fileData.status === 'uploaded' ? 'uploaded' : fileData.status;

            return (
              <FileUploadStatusWrapper
                key={fileData.id}
                fileData={{
                  id: fileData.id,
                  file: fileData.file,
                  processedAt:
                    fileData.status === 'uploaded' ? new Date() : undefined,
                }}
                status={uiStatus as any}
                progress={fileData.progress}
                onDelete={handleFileDelete}
                onRetry={
                  fileData.status === 'failed'
                    ? () => handleFileRetry(fileData.id)
                    : undefined
                }
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default DocumentUploadManager;
